/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  hasMany,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array } from 'ember-data-model-fragments/attributes';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import type Assessment from 'linced-admin/models/assessments/assessment';
import type Subject from 'linced-admin/models/goals/subject';
import type Group from 'linced-admin/models/group';
import type Set from 'linced-admin/models/milestones/set';
import type Page from 'linced-admin/models/posts/page';
import type TagCategory from 'linced-admin/models/posts/tag-category';

// Proto: schools.UsageSet.
export default class UsageSet extends Model {
  declare [Type]: 'schools/usage-set';

  @attr('number')
  declare createTime: number | undefined;

  @attr('number')
  declare updateTime: number | undefined;

  @attr('string', { defaultValue: '' })
  declare name: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @array('number')
  declare jurisdictions: StatefulArray<number>;

  @hasMany('posts/page', { async: true, inverse: 'usageSetIds' })
  declare pageIds: AsyncHasMany<Page>;

  @computed('pageIds.content.[]')
  get pagesArray() {
    return resolved(this.pageIds);
  }

  @hasMany('goals/subject', { async: true, inverse: 'usageSetIds' })
  declare goalSubjectIds: AsyncHasMany<Subject>;

  @computed('goalSubjectIds.content.[]')
  get goalSubjectsArray() {
    return resolved(this.goalSubjectIds);
  }

  @hasMany('milestones/set', { async: true, inverse: 'usageSetIds' })
  declare milestoneSetIds: AsyncHasMany<Set>;

  @computed('milestoneSetIds.content.[]')
  get milestoneSetsArray() {
    return resolved(this.milestoneSetIds);
  }

  @hasMany('group', { async: true, inverse: 'usageSetIds' })
  declare groupTemplateIds: AsyncHasMany<Group>;

  @computed('groupTemplateIds.content.[]')
  get groupTemplatesArray() {
    return resolved(this.groupTemplateIds);
  }

  @hasMany('assessments/assessment', { async: true, inverse: 'usageSetIds' })
  declare assessmentTemplateIds: AsyncHasMany<Assessment>;

  @computed('assessmentTemplateIds.content.[]')
  get assessmentTemplatesArray() {
    return resolved(this.assessmentTemplateIds);
  }

  @hasMany('posts/tag-category', { async: true, inverse: 'usageSetIds' })
  declare tagCategoryTemplatesIds: AsyncHasMany<TagCategory>;

  @computed('tagCategoryTemplatesIds.content.[]')
  get tagCategoryTemplatesArray() {
    return resolved(this.tagCategoryTemplatesIds);
  }

  // displayedJurisdictions converts numeric values into Jurisdiction enum objects
  // for editing purposes
  @computed('jurisdictions.[]')
  get displayedJurisdictions() {
    if (isEmpty(this.jurisdictions)) {
      return [];
    }
    const enumObjectsArray = this.jurisdictions.map((value) => {
      return Object.values(Jurisdiction.values).find(
        (obj) => obj.value === value,
      );
    });
    return enumObjectsArray;
  }
}
